<!-- AddCouponReservationForm.vue -->
<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useCouponReservationStore } from "@/stores/useCouponReservationStore";
import { useCouponStore } from "@/stores/useCouponStore";
import { useUserStore } from "@/stores/useUserStore";

const reservationStore = useCouponReservationStore();
const couponStore = useCouponStore();
const userStore = useUserStore();

import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// ====================================================================
// ESTADO DO FORMULÁRIO
// ====================================================================
const form = ref({
  couponId: null,
  userId: null,
  subscriptionId: "", // opcional — campo transacional do checkout
  expiresAt: "",
});

const errors = ref({});
const isSubmitting = ref(false);

// ====================================================================
// AUTOCOMPLETE — CUPOM (sem filtro de regra)
// ====================================================================
// ✅ Reservas aceitam cupons de qualquer regra (ANYONE, TARGETED, FIRST_PAID).
//    Não filtramos no autocomplete — diferentemente do AddCouponUserForm.
const couponSearch = ref("");
const couponResults = ref([]);
const couponLoading = ref(false);
const showCouponDropdown = ref(false);
const selectedCouponLabel = ref("");
let couponDebounce = null;

function formatEligibleRule(rule) {
  const map = {
    ANYONE: "Anyone",
    TARGETED: "Targeted",
    FIRST_PAID: "First Paid",
  };
  return map[rule] ?? rule ?? "";
}

async function onCouponInput() {
  form.value.couponId = null;
  selectedCouponLabel.value = "";
  const q = couponSearch.value.trim();
  if (!q) {
    couponResults.value = [];
    showCouponDropdown.value = false;
    return;
  }
  couponLoading.value = true;
  clearTimeout(couponDebounce);
  couponDebounce = setTimeout(async () => {
    try {
      // fetchCouponsPaged(page, size, filters) — só search, sem restrição de regra.
      await couponStore.fetchCouponsPaged(1, 10, {
        search: q,
        status: "",
        eligibleRule: "",
        applyToAllCycles: "",
      });
      couponResults.value = couponStore.coupons;
      showCouponDropdown.value = couponResults.value.length > 0;
    } catch {
      couponResults.value = [];
    } finally {
      couponLoading.value = false;
    }
  }, 300);
}

function selectCoupon(coupon) {
  form.value.couponId = coupon.couponId;
  const pct =
    coupon.discountPercent != null ? `${coupon.discountPercent}%` : "";
  selectedCouponLabel.value = pct
    ? `${coupon.code} · ${pct}`
    : coupon.code || `#${coupon.couponId}`;
  couponSearch.value = selectedCouponLabel.value;
  showCouponDropdown.value = false;
  couponResults.value = [];
}

function clearCoupon() {
  form.value.couponId = null;
  couponSearch.value = "";
  selectedCouponLabel.value = "";
  couponResults.value = [];
  showCouponDropdown.value = false;
}

// ====================================================================
// AUTOCOMPLETE — USUÁRIO
// ====================================================================
const userSearch = ref("");
const userResults = ref([]);
const userLoading = ref(false);
const showUserDropdown = ref(false);
const selectedUserLabel = ref("");
let userDebounce = null;

async function onUserInput() {
  form.value.userId = null;
  selectedUserLabel.value = "";
  const q = userSearch.value.trim();
  if (!q) {
    userResults.value = [];
    showUserDropdown.value = false;
    return;
  }
  userLoading.value = true;
  clearTimeout(userDebounce);
  userDebounce = setTimeout(async () => {
    try {
      await userStore.fetchUsers(1, 10, q);
      userResults.value = userStore.users;
      showUserDropdown.value = userResults.value.length > 0;
    } catch {
      userResults.value = [];
    } finally {
      userLoading.value = false;
    }
  }, 300);
}

function selectUser(user) {
  form.value.userId = user.userId;
  selectedUserLabel.value = user.fullName || user.email || `#${user.userId}`;
  userSearch.value = selectedUserLabel.value;
  showUserDropdown.value = false;
  userResults.value = [];
}

function clearUser() {
  form.value.userId = null;
  userSearch.value = "";
  selectedUserLabel.value = "";
  userResults.value = [];
  showUserDropdown.value = false;
}

// ====================================================================
// FECHAR DROPDOWNS AO CLICAR FORA
// ====================================================================
function handleOutsideClick(e) {
  if (!e.target.closest(".autocomplete-wrap")) {
    showCouponDropdown.value = false;
    showUserDropdown.value = false;
  }
}

// ====================================================================
// TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 3000);
}
function showErrorToast(msg) {
  toastType.value = "error";
  toastMessage.value = msg;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(msg) {
  toastType.value = "success";
  toastMessage.value = msg;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// LIFECYCLE
// ====================================================================
onMounted(async () => {
  document.addEventListener("keydown", handleEscKey);
  document.addEventListener("mousedown", handleOutsideClick);
  await nextTick();
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("mousedown", handleOutsideClick);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  clearTimeout(couponDebounce);
  clearTimeout(userDebounce);
});

function handleEscKey(e) {
  if (e.key === "Escape") closeModal();
}

// ====================================================================
// VALIDAÇÃO
// ====================================================================
function validateForm() {
  errors.value = {};

  if (!form.value.couponId) errors.value.couponId = "Selecione um cupom.";

  if (!form.value.userId) errors.value.userId = "Selecione um usuário.";

  if (!form.value.expiresAt) {
    errors.value.expiresAt = "A data de expiração é obrigatória.";
  } else {
    const expiresDate = new Date(form.value.expiresAt);
    if (Number.isNaN(expiresDate.getTime()) || expiresDate <= new Date()) {
      errors.value.expiresAt = "A data de expiração deve ser uma data futura.";
    }
  }

  // SubscriptionId é opcional — se preenchido, precisa ser inteiro positivo
  const subIdRaw = form.value.subscriptionId;
  if (subIdRaw !== "" && subIdRaw !== null && subIdRaw !== undefined) {
    const subId = Number(subIdRaw);
    if (!Number.isInteger(subId) || subId < 1) {
      errors.value.subscriptionId =
        "Deve ser um inteiro positivo, ou vazio se a reserva ainda não está vinculada.";
    }
  }

  return Object.keys(errors.value).length === 0;
}

// ====================================================================
// SUBMIT
// ====================================================================
async function handleSubmit() {
  if (isSubmitting.value) return;

  if (!validateForm()) {
    showErrorToast("Verifique os campos obrigatórios antes de salvar.");
    return;
  }

  isSubmitting.value = true;

  try {
    const subIdRaw = form.value.subscriptionId;
    const payload = {
      couponId: form.value.couponId,
      userId: form.value.userId,
      subscriptionId:
        subIdRaw === "" || subIdRaw === null || subIdRaw === undefined
          ? null
          : Number(subIdRaw),
      expiresAt: form.value.expiresAt || null,
    };

    await reservationStore.createCouponReservation(payload);
    showSuccessToast("Reserva criada com sucesso.");

    setTimeout(() => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    }, 2400);
  } catch (error) {
    console.error("❌ Erro ao criar reserva:", error);
    const data = error?.response?.data;
    let msg = "Ocorreu um erro ao criar a reserva.";
    if (data?.errors) {
      const msgs = Object.values(data.errors).flat().filter(Boolean);
      if (msgs.length) msg = msgs.join("\n");
    } else if (data?.message) {
      msg = data.message;
    } else if (error?.message) {
      msg = error.message;
    }
    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// FECHAR
// ====================================================================
function closeModal() {
  if (!isSubmitting.value) emit("close");
}
</script>

<template>
  <div class="modal-backdrop" @click.self="closeModal">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <!-- HEADER -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Criar Reserva de Cupom</h2>
        <button
          @click="closeModal"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0
                 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
                 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293
                 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" novalidate>
        <div class="modal-body">
          <!-- Aviso: criação manual é exceção -->
          <div class="info-banner">
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z"
              />
            </svg>
            <span>
              Em fluxo normal, reservas são criadas automaticamente no checkout.
              Esta criação manual é destinada apenas a ajustes administrativos.
            </span>
          </div>

          <!-- LINHA 1: Cupom + Usuário -->
          <div class="form-grid-2" style="margin-top: 1rem">
            <!-- Cupom (autocomplete — sem filtro) -->
            <div class="form-group autocomplete-wrap">
              <label class="form-label">
                Cupom <span class="required-star">*</span>
              </label>
              <div class="autocomplete-input-wrap">
                <input
                  type="text"
                  v-model="couponSearch"
                  @input="onCouponInput"
                  @focus="showCouponDropdown = couponResults.length > 0"
                  class="input-base"
                  :class="{
 'is-invalid': errors.couponId,
                    'is-selected': !!form.couponId,
                  }"
                  placeholder="Buscar por código…"
                  autocomplete="off"
                />
                <button
                  v-if="form.couponId"
                  type="button"
                  class="clear-btn"
                  @click="clearCoupon"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
                <span v-if="couponLoading" class="spinner-inline" />
              </div>
              <ul v-if="showCouponDropdown" class="dropdown-list">
                <li
                  v-for="c in couponResults"
                  :key="c.couponId"
                  @mousedown.prevent="selectCoupon(c)"
                  class="dropdown-item"
                >
                  <span class="dropdown-main">
                    {{ c.code }}
                    <span v-if="c.discountPercent != null" class="billing-badge"
                      >{{ c.discountPercent }}%</span
                    >
                  </span>
                  <span class="dropdown-sub">
                    <template v-if="c.eligibleRule"
                      >{{ formatEligibleRule(c.eligibleRule) }} · </template
                    >#{{ c.couponId }}
                    <template v-if="c.isActive === false"> · inativo</template>
                  </span>
                </li>
              </ul>
              <span v-if="errors.couponId" class="error-msg">{{
                errors.couponId
              }}</span>
            </div>

            <!-- Usuário (autocomplete) -->
            <div class="form-group autocomplete-wrap">
              <label class="form-label">
                Nome do Usuário <span class="required-star">*</span>
              </label>
              <div class="autocomplete-input-wrap">
                <input
                  type="text"
                  v-model="userSearch"
                  @input="onUserInput"
                  @focus="showUserDropdown = userResults.length > 0"
                  class="input-base"
                  :class="{
 'is-invalid': errors.userId,
                    'is-selected': !!form.userId,
                  }"
                  placeholder="Buscar por nome ou e-mail…"
                  autocomplete="off"
                />
                <button
                  v-if="form.userId"
                  type="button"
                  class="clear-btn"
                  @click="clearUser"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
                <span v-if="userLoading" class="spinner-inline" />
              </div>
              <ul v-if="showUserDropdown" class="dropdown-list">
                <li
                  v-for="u in userResults"
                  :key="u.userId"
                  @mousedown.prevent="selectUser(u)"
                  class="dropdown-item"
                >
                  <span class="dropdown-main">{{
                    u.fullName || u.userName
                  }}</span>
                  <span class="dropdown-sub"
                    >{{ u.email }} · #{{ u.userId }}</span
                  >
                </li>
              </ul>
              <span v-if="errors.userId" class="error-msg">{{
                errors.userId
              }}</span>
            </div>
          </div>

          <!-- LINHA 2: Expira em (coluna única) -->
          <div class="form-grid-1" style="margin-top: 1rem">
            <div class="form-group">
              <label class="form-label">
                Expira em <span class="required-star">*</span>
              </label>
              <input
                type="datetime-local"
                v-model="form.expiresAt"
                class="input-base"
                :class="{ 'is-invalid': errors.expiresAt }"
              />
              <span v-if="errors.expiresAt" class="error-msg">{{
                errors.expiresAt
              }}</span>
              <span v-else class="hint-msg">
                Deve ser uma data futura. Após este momento, a reserva expira se
                não for consumida ou cancelada.
              </span>
            </div>
          </div>

          <!-- LINHA 3: SubscriptionId (transacional) -->
          <div class="form-grid-1 txn-group" style="margin-top: 1rem">
            <div class="form-group">
              <label class="form-label">
                ID da Assinatura
                <span class="optional-tag">opcional</span>
                <span class="txn-hint">preenchido pelo checkout</span>
              </label>
              <input
                type="number"
                v-model="form.subscriptionId"
                class="input-base input-base-txn"
                min="1"
                step="1"
                placeholder="Vazio = reserva solta"
                :class="{ 'is-invalid': errors.subscriptionId }"
              />
              <span v-if="errors.subscriptionId" class="error-msg">{{
                errors.subscriptionId
              }}</span>
              <span v-else class="hint-msg">
                Normalmente preenchido automaticamente quando a assinatura é
                criada. Preencha apenas para vincular manualmente a uma
                assinatura existente.
              </span>
            </div>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            <span>{{ isSubmitting ? "Criando..." : "Criar Reserva" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- TOAST -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button type="button" class="toast-close" @click="showToast = false">
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 38rem;
  max-height: calc(100vh - 40px);
}
.modal-panel > form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.modal-header {
  flex-shrink: 0;
}

.modal-footer {
  flex-shrink: 0;
}

/* GRIDS */
.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}
@media (min-width: 540px) {
  .form-grid-2 {
    grid-template-columns: 1fr 1fr;
  }
}
.form-grid-1 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.required-star {
  color: var(--danger);
  font-weight: 700;
}
.optional-tag {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-muted);
  background-color: var(--surface-3);
  border: 1px solid var(--neutral-200);
  border-radius: 4px;
  padding: 0.05rem 0.4rem;
}

/* ─── Destaque transacional (campos do checkout) ─── */
.txn-hint {
  font-size: 0.68rem;
  font-weight: 500;
  color: #b45309;
  background-color: var(--warning-bg);
  padding: 1px 6px;
  border-radius: 4px;
  font-style: italic;
}
.txn-group {
  padding: 0.6rem 0.75rem;
  background-color: var(--warning-bg);
  border-left: 3px solid #fbbf24;
  border-radius: 6px;
}

.input-base.is-selected {
  border-color: #86efac;
  background-color: #f0fdf4;
}
.input-base-txn {
  border-color: var(--warning-bg);
}

/* AUTOCOMPLETE */
.autocomplete-wrap {
  position: relative;
}
.autocomplete-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.autocomplete-input-wrap .input-base {
  padding-right: 2rem;
}

.clear-btn {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.1rem;
  color: var(--text-subtle);
  display: flex;
  align-items: center;
}
.clear-btn:hover {
  color: var(--danger);
}
.clear-btn svg {
  width: 0.9rem;
  height: 0.9rem;
}

.spinner-inline {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 0.85rem;
  height: 0.85rem;
  border: 2px solid var(--neutral-200);
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin {
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
  max-height: 180px;
  overflow-y: auto;
  padding: 0.25rem 0;
  list-style: none;
}

.dropdown-main {
  font-size: 0.875rem;
  color: var(--text-dark);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.billing-badge {
  font-size: 0.68rem;
  font-weight: 600;
  color: #7c3aed;
  background-color: var(--brand-soft);
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
  letter-spacing: 0.02em;
}
.dropdown-sub {
  font-size: 0.75rem;
  color: var(--text-subtle);
}

/* MENSAGENS */
.error-msg {
  color: var(--danger);
  font-size: 0.78rem;
}
.hint-msg {
  color: var(--text-subtle);
  font-size: 0.75rem;
  margin-top: 0.1rem;
}

/* INFO BANNER */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background-color: var(--info-bg);
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 0.8rem;
  color: var(--link-hover);
}

</style>